import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../contexts/ToastContext';

export interface FileData {
  file: File;
  fileBlobUrl: string;
  requestId: string;
}

export enum FileInputHandlerMode {
  Append = 'append',
  Replace = 'replace',
}

export function useFileInputHandler(
  options: {
    fileSizeLimitInMb?: number;
    mode?: FileInputHandlerMode;
  } = {},
): {
  fileData: FileData[];
  addFiles: (files: File[]) => FileData[] | undefined;
  fileInputChangeHandler: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => FileData[] | undefined;
  removeFileData: (requestId: string) => void;
} {
  const [fileData, setFileData] = useState<FileData[]>([]);
  const { error: showErrorToast } = useToast();
  const { mode = FileInputHandlerMode.Replace, fileSizeLimitInMb } = options;

  const addFiles = useCallback(
    (files: File[]) => {
      // If a size limit is set, check for oversized files
      if (fileSizeLimitInMb) {
        const errorFileNames = files.reduce((acc, file) => {
          const fileSizeInMb = file.size / 1024 / 1024;
          if (fileSizeInMb > fileSizeLimitInMb) {
            acc.push(file.name);
          }
          return acc;
        }, [] as string[]);

        if (errorFileNames.length > 0) {
          showErrorToast(
            `${errorFileNames.length} file(s) are too large. Please ensure all files are smaller than ${fileSizeLimitInMb}MB.`,
            { dontAutoHide: true },
          );
          return undefined;
        }
      }

      const mappedFileData = files.map((file) => ({
        file,
        fileBlobUrl: URL.createObjectURL(file),
        requestId: uuidv4(), // unique identifier so we can track the file in all stages of uploading
      }));

      setFileData((prev) => {
        if (mode === FileInputHandlerMode.Replace) {
          prev.forEach((fd) => URL.revokeObjectURL(fd.fileBlobUrl));
          return mappedFileData;
        }
        return [...prev, ...mappedFileData];
      });

      return mappedFileData;
    },
    [fileSizeLimitInMb, showErrorToast, mode],
  );

  const fileInputChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (!files) return undefined;

      const addedFileData = addFiles(Array.from(files));
      if (!addedFileData) {
        e.target.value = '';
      }
      return addedFileData;
    },
    [addFiles],
  );

  const removeFileData = useCallback((requestId: string) => {
    setFileData((prev) => {
      const removed = prev.find((fd) => fd.requestId === requestId);
      if (removed) URL.revokeObjectURL(removed.fileBlobUrl);
      return prev.filter((fd) => fd.requestId !== requestId);
    });
  }, []);

  useEffect(
    () => () => {
      setFileData((prev) => {
        prev.forEach((fd) => URL.revokeObjectURL(fd.fileBlobUrl));
        return [];
      });
    },
    [],
  );

  return { fileData, addFiles, fileInputChangeHandler, removeFileData };
}
